/**
 * Workspace Status Page
 *
 * Live health checks for all backing services used by this workspace:
 *   - Integration Engine  (CANONICAL_ENV.integrations, prod: integrations-engine.vercel.app)
 *   - LLM Engine          (CANONICAL_ENV.llm or same-origin /api/llm)
 *   - Supabase            (derived from current session)
 *
 * Route: /prioritization/settings/status
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Circle,
  AlertTriangle,
  Zap,
  Link2,
  Database,
  Copy,
  Check,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getAIProvider } from './Connectors';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { INTEGRATIONS_ENGINE_URL as INTEGRATION_ENGINE_URL } from '../../../lib/engineUrls';
import { CANONICAL_ENV } from '../../../lib/envConfig';

// ── Config ───────────────────────────────────────────────────────────────────
const LLM_ENGINE_DIRECT_URL  = 'http://localhost:3004';
const LLM_ENGINE_PROXY_PATH  = '/api/llm';
const LLM_API_URL_OVERRIDE   = import.meta.env.VITE_LLM_API_URL as string | undefined;

// ── Types ────────────────────────────────────────────────────────────────────

type ServiceStatus = 'checking' | 'online' | 'degraded' | 'offline';

interface ServiceState {
  status: ServiceStatus;
  latencyMs: number | null;
  detail: string;
  checkedAt: Date | null;
}

function defaultState(): ServiceState {
  return { status: 'checking', latencyMs: null, detail: 'Checking…', checkedAt: null };
}

// ── Health probe helpers ──────────────────────────────────────────────────────

async function probeUrl(url: string, timeoutMs = 3000): Promise<{ ok: boolean; latencyMs: number; body?: unknown }> {
  const t0 = performance.now();
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(timeoutMs) });
    const latencyMs = Math.round(performance.now() - t0);
    let body: unknown;
    try { body = await res.json(); } catch { /* non-JSON */ }
    return { ok: res.ok, latencyMs, body };
  } catch {
    return { ok: false, latencyMs: Math.round(performance.now() - t0) };
  }
}

/**
 * Like probeUrl but considers ANY HTTP response (including 4xx/5xx) as
 * "reachable" — a non-200 still proves the process is running.
 * Only a connection refusal / timeout counts as unreachable.
 */
async function probeReachable(url: string, timeoutMs = 3000): Promise<{ reachable: boolean; latencyMs: number }> {
  const t0 = performance.now();
  try {
    await fetch(url, { signal: AbortSignal.timeout(timeoutMs) });
    return { reachable: true, latencyMs: Math.round(performance.now() - t0) };
  } catch {
    return { reachable: false, latencyMs: Math.round(performance.now() - t0) };
  }
}

async function checkIntegrationEngine(): Promise<ServiceState> {
  const { ok, latencyMs, body } = await probeUrl(`${INTEGRATION_ENGINE_URL}/health`);
  const checkedAt = new Date();
  if (ok) {
    const version = (body as { version?: string })?.version;
    return {
      status: 'online',
      latencyMs,
      detail: version ? `v${version} · ${latencyMs}ms` : `Reachable · ${latencyMs}ms`,
      checkedAt,
    };
  }
  return { status: 'offline', latencyMs, detail: `Not reachable at ${INTEGRATION_ENGINE_URL}`, checkedAt };
}

async function checkLLMEngine(): Promise<ServiceState> {
  const checkedAt = new Date();
  // Prefer env-override, then proxy path, then direct localhost
  const candidates = LLM_API_URL_OVERRIDE
    ? [`${LLM_API_URL_OVERRIDE.replace(/\/$/, '')}/health`]
    : [`${LLM_ENGINE_PROXY_PATH}/health`, `${LLM_ENGINE_DIRECT_URL}/health`];

  for (const url of candidates) {
    const { reachable, latencyMs } = await probeReachable(url, 3000);
    if (reachable) {
      const provider = getAIProvider();
      const providerLabel = provider === 'anthropic'
        ? 'Claude (Anthropic) active'
        : provider === 'openai'
          ? 'ChatGPT (OpenAI) active'
          : 'No provider configured';
      return {
        status: 'online',
        latencyMs,
        detail: `${providerLabel} · ${latencyMs}ms`,
        checkedAt,
      };
    }
  }

  // LLM engine may be offline but API key features still work via direct OpenAI
  const keyPresent = !!import.meta.env.VITE_OPENAI_API_KEY || !!import.meta.env.VITE_ANTHROPIC_API_KEY;
  if (keyPresent) {
    return {
      status: 'degraded',
      latencyMs: null,
      detail: 'peak-llm not running — direct API key features may still work',
      checkedAt,
    };
  }
  return {
    status: 'offline',
    latencyMs: null,
    detail: `Not reachable at ${LLM_API_URL_OVERRIDE ?? LLM_ENGINE_DIRECT_URL}`,
    checkedAt,
  };
}

async function checkSupabase(): Promise<ServiceState> {
  const checkedAt = new Date();
  const t0 = performance.now();
  try {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      return { status: 'offline', latencyMs: null, detail: 'Supabase client not initialised', checkedAt };
    }
    // Lightweight round-trip: check auth session (doesn't require a table query)
    const { error } = await supabase.auth.getSession();
    const latencyMs = Math.round(performance.now() - t0);
    if (error) {
      return { status: 'degraded', latencyMs, detail: `Session error: ${error.message}`, checkedAt };
    }
    return { status: 'online', latencyMs, detail: `Connected · ${latencyMs}ms`, checkedAt };
  } catch (e) {
    return {
      status: 'offline',
      latencyMs: Math.round(performance.now() - t0),
      detail: e instanceof Error ? e.message : 'Unknown error',
      checkedAt,
    };
  }
}

// ── Status indicator ──────────────────────────────────────────────────────────

function StatusDot({ status }: { status: ServiceStatus }) {
  if (status === 'checking') return <Circle className="h-3 w-3 text-[var(--text-ter)] animate-pulse" />;
  if (status === 'online')   return <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--green)] opacity-50" /><span className="relative inline-flex rounded-full h-3 w-3 bg-[var(--green)]" /></span>;
  if (status === 'degraded') return <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--mustard)] opacity-50" /><span className="relative inline-flex rounded-full h-3 w-3 bg-[var(--mustard)]" /></span>;
  return <span className="relative flex h-3 w-3"><span className="relative inline-flex rounded-full h-3 w-3 bg-[var(--coral)]" /></span>;
}

function statusLabel(s: ServiceStatus) {
  if (s === 'checking') return { text: 'Checking', cls: 'text-[var(--text-ter)]' };
  if (s === 'online')   return { text: 'Online',   cls: 'text-[var(--green)] dark:text-[var(--text-ter)]' };
  if (s === 'degraded') return { text: 'Degraded', cls: 'text-[var(--mustard)]' };
  return                       { text: 'Offline',  cls: 'text-[var(--coral)]' };
}

// ── Service card ──────────────────────────────────────────────────────────────

function ServiceCard({
  icon: Icon,
  iconBg,
  iconColor,
  name,
  description,
  state,
  url,
  docs,
}: {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  name: string;
  description: string;
  state: ServiceState;
  url?: string;
  docs?: string;
}) {
  const label = statusLabel(state.status);
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10  ${iconBg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[var(--text-pri)]">{name}</h3>
              {url && (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--text-ter)] hover:text-primary-600 transition-colors"
                  title={`Open ${url}`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">{description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusDot status={state.status} />
          <span className={`text-xs font-semibold ${label.cls}`}>{label.text}</span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-xs text-[var(--text-ter)]">{state.detail}</p>
        {state.checkedAt && (
          <span className="text-[10px] text-[var(--text-ter)] flex-shrink-0 ml-2">
            Checked {state.checkedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        )}
      </div>

      {docs && (
        <div className="mt-3 pt-3 border-t border-[var(--rule)]">
          <a
            href={docs}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary-600 hover:underline"
          >
            Setup docs →
          </a>
        </div>
      )}
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

export default function WorkspaceStatus() {
  const [integrationEngine, setIntegrationEngine] = useState<ServiceState>(defaultState);
  const [llmEngine, setLlmEngine] = useState<ServiceState>(defaultState);
  const [supabase, setSupabase] = useState<ServiceState>(defaultState);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [copied, setCopied] = useState(false);
  const workspaceId = getCurrentWorkspaceId();

  const copyWorkspaceId = useCallback(() => {
    if (!workspaceId) return;
    void navigator.clipboard.writeText(workspaceId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [workspaceId]);

  const checkAll = useCallback(async () => {
    setIsRefreshing(true);
    setIntegrationEngine(defaultState());
    setLlmEngine(defaultState());
    setSupabase(defaultState());

    const [ie, llm, sb] = await Promise.all([
      checkIntegrationEngine(),
      checkLLMEngine(),
      checkSupabase(),
    ]);

    setIntegrationEngine(ie);
    setLlmEngine(llm);
    setSupabase(sb);
    setLastRefreshed(new Date());
    setIsRefreshing(false);
  }, []);

  // Run on mount and auto-refresh every 30 seconds
  useEffect(() => {
    checkAll();
    const interval = setInterval(checkAll, 30_000);
    return () => clearInterval(interval);
  }, [checkAll]);

  const allOnline  = [integrationEngine, llmEngine, supabase].every((s) => s.status === 'online');
  const anyOffline = [integrationEngine, llmEngine, supabase].some((s) => s.status === 'offline');
  const anyDegraded = [integrationEngine, llmEngine, supabase].some((s) => s.status === 'degraded');

  const overallStatus: ServiceStatus = anyOffline ? 'offline' : anyDegraded ? 'degraded' : allOnline ? 'online' : 'checking';

  const overallBanner = {
    online:   { bg: 'bg-[var(--green-dim)] border-[var(--green)]',  icon: CheckCircle2,   iconCls: 'text-[var(--green)]', text: 'All systems operational', textCls: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
    degraded: { bg: 'bg-[var(--mustard-dim)] border-[var(--mustard)]',   icon: AlertTriangle,  iconCls: 'text-[var(--mustard)]', text: 'Some services degraded',  textCls: 'text-[var(--mustard)]' },
    offline:  { bg: 'bg-[var(--coral-dim)] border-[var(--coral)]',       icon: XCircle,        iconCls: 'text-[var(--coral)]',   text: 'One or more services offline', textCls: 'text-[var(--coral)]' },
    checking: { bg: 'bg-[var(--surface)] border-[var(--rule)]',     icon: Circle,         iconCls: 'text-[var(--text-ter)]',  text: 'Checking services…',     textCls: 'text-[var(--text-sec)]' },
  }[overallStatus];

  const BannerIcon = overallBanner.icon;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Activity className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Status"
        subtitle="Live health of all backing services for this workspace"
        actions={
          <button
            type="button"
            onClick={checkAll}
            disabled={isRefreshing}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* Overall banner */}
      <div className={`flex items-center gap-3 px-4 py-3 border  ${overallBanner.bg}`}>
        <BannerIcon className={`h-5 w-5 flex-shrink-0 ${overallBanner.iconCls}`} />
        <span className={`text-sm font-semibold ${overallBanner.textCls}`}>{overallBanner.text}</span>
        {lastRefreshed && (
          <span className="ml-auto text-xs text-[var(--text-ter)] flex-shrink-0">
            Auto-refreshes every 30s · Last checked {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        )}
      </div>

      {/* Service cards */}
      <div className="grid grid-cols-1 gap-4">
        <ServiceCard
          icon={Link2}
          iconBg="bg-[var(--slate-dim)]"
          iconColor="text-[var(--text-ter)]"
          name="Integration Engine"
          description={`Connector backend — Miro, Jira, Slack, calendar sync · ${INTEGRATION_ENGINE_URL}`}
          state={integrationEngine}
          url={integrationEngine.status === 'online' ? INTEGRATION_ENGINE_URL : undefined}
        />

        <ServiceCard
          icon={Zap}
          iconBg="bg-[var(--slate-dim)]"
          iconColor="text-[var(--text-ter)]"
          name="LLM Engine (peak-llm)"
          description={`AI features — clustering, item enrichment, idea crafting · ${LLM_API_URL_OVERRIDE ?? `${LLM_ENGINE_DIRECT_URL} / ${LLM_ENGINE_PROXY_PATH}`}`}
          state={llmEngine}
          url={llmEngine.status === 'online' ? (LLM_API_URL_OVERRIDE ?? LLM_ENGINE_DIRECT_URL) : undefined}
        />

        <ServiceCard
          icon={Database}
          iconBg="bg-[var(--green-dim)]"
          iconColor="text-[var(--green)] dark:text-[var(--text-ter)]"
          name="Supabase"
          description="Primary database, auth, and realtime subscriptions"
          state={supabase}
        />
      </div>

      {/* Configuration notes */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5 space-y-3">
        <h3 className="text-sm font-semibold text-[var(--text-sec)]">Configuration</h3>
        <div className="space-y-2 text-xs text-[var(--text-ter)]">
          <div className="flex items-center gap-2">
            <span className="font-medium text-[var(--text-sec)]">Workspace ID</span>
            {workspaceId ? (
              <>
                <code className="bg-[var(--hover-tint)] px-1.5 py-0.5 rounded text-[var(--text-sec)] select-all">{workspaceId}</code>
                <button
                  type="button"
                  onClick={copyWorkspaceId}
                  title="Copy workspace ID"
                  className="text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
                >
                  {copied
                    ? <Check className="h-3.5 w-3.5 text-[var(--green)]" />
                    : <Copy className="h-3.5 w-3.5" />
                  }
                </button>
              </>
            ) : (
              <span className="text-[var(--text-ter)] italic">Not available — sign in to a workspace</span>
            )}
          </div>
          <p>
            <span className="font-medium text-[var(--text-sec)]">Integration Engine</span> — start with{' '}
            <code className="bg-[var(--hover-tint)] px-1.5 py-0.5 rounded text-[var(--text-sec)]">cd engines/integrations-engine && npm run dev</code>.
            Local port <code className="bg-[var(--hover-tint)] px-1.5 py-0.5 rounded text-[var(--text-sec)]">4100</code>; production:{' '}
            <code className="bg-[var(--hover-tint)] px-1.5 py-0.5 rounded text-[var(--text-sec)]">{CANONICAL_ENV.integrations}</code> → integrations-engine.vercel.app
            {' '}<Link to="/prioritization/settings/connectors" className="text-primary-600 hover:underline">Configure connectors →</Link>
          </p>
          <p>
            <span className="font-medium text-[var(--text-sec)]">LLM Engine</span> — start with{' '}
            <code className="bg-[var(--hover-tint)] px-1.5 py-0.5 rounded text-[var(--text-sec)]">cd peak-llm && npm start</code>.
            Runs on <code className="bg-[var(--hover-tint)] px-1.5 py-0.5 rounded text-[var(--text-sec)]">:3004</code> by default, or set{' '}
            <code className="bg-[var(--hover-tint)] px-1.5 py-0.5 rounded text-[var(--text-sec)]">VITE_LLM_API_URL</code> in{' '}
            <code className="bg-[var(--hover-tint)] px-1.5 py-0.5 rounded text-[var(--text-sec)]">.env.local</code>.
            Active AI provider:{' '}
            <span className="font-medium text-[var(--text-sec)]">
              {getAIProvider() === 'anthropic'
                ? 'Claude (Anthropic)'
                : getAIProvider() === 'openai'
                  ? 'ChatGPT (OpenAI)'
                  : 'None — add an API key in Connectors'}
            </span>
            {' '}—{' '}
            <Link to="/prioritization/settings/connectors" className="text-primary-600 hover:underline">change provider →</Link>
          </p>
          <p>
            <span className="font-medium text-[var(--text-sec)]">Supabase</span> — configured via{' '}
            <code className="bg-[var(--hover-tint)] px-1.5 py-0.5 rounded text-[var(--text-sec)]">VITE_SUPABASE_URL</code> and{' '}
            <code className="bg-[var(--hover-tint)] px-1.5 py-0.5 rounded text-[var(--text-sec)]">VITE_SUPABASE_ANON_KEY</code>{' '}
            in <code className="bg-[var(--hover-tint)] px-1.5 py-0.5 rounded text-[var(--text-sec)]">.env.local</code>.
          </p>
        </div>
      </div>
    </div>
  );
}
